import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import API from "@/app/api";
import { storage } from "@/lib/storage";
import { login, verifyStoredUserData } from "@/lib/auth";
import { CheckPath, BtnText } from "@/lib/login/loginFlowConst";
import OtpInput from "react-otp-input";
import {
  ValidateMobileNumber,
  validateEmail,
  NameValidation,
} from "../../lib/login/validators";
import ModalLayout from "../ui/ModalLayout";
import ModalLayoutBottomFixed from "../ui/ModalLayoutBottomFixed";
import useIsMobile from "@/hooks/useIsMobile";
import CommonText from "../text/CommonText";
import heroBg from "../../assets/login/login.png";
import logo from "../../assets/logo.png";
import Image from "next/image";
import { rzploadScript } from "@/lib/rzploadScript";
import { track } from "@/lib/tracking";

const ModalContent = ({
  formData,
  text,
  disableBtn,
  otp,
  handleInpChange,
  handleKeyDown,
  handleLoginBtn,
  setOtp,
  otpError,
}: {
  formData: {
    phoneNo: string;
    user_name: string;
    email: string;
    id: string;
  };
  text: {
    btnText: BtnText;
    checkPath: CheckPath;
  };
  disableBtn: boolean;
  otp: string;
  handleInpChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleLoginBtn: () => void;
  setOtp: (value: string) => void;
  otpError: string;
}) => {

  return (
    <div className="w-full h-auto flex items-start flex-wrap md:flex-nowrap">
      {/* Left Side */}
      <Image
        src={heroBg}
        className="hidden md:block w-1/2 h-[300px] md:h-[500px] bg-center z-0 rounded-tl-xl rounded-bl-xl object-fill"
        alt="hero-bg"
        width={350}
        height={500}
      />
      {/* Right Side */}
      <div className="w-full md:w-1/2 h-full px-4 md:px-8 py-4 md:py-0 bg-white md:mt-10">
        {/* Login */}
        {text.checkPath === CheckPath.LoginPage && (
          <>
            <CommonText size="base" className="text-gray-500 font-bold">Sign in</CommonText>
            <CommonText size="xl" className="font-medium mt-4">
              Login to continue booking your ride!
            </CommonText>
            {/* <CommonText size="xs" className="mt-4 mb-1 text-gray-500">
              Mobile Number
            </CommonText> */}
            <div className="flex items-center gap-2 w-full mt-6">
              <div className="px-6 border border-gray-400 rounded-full h-[40px] flex items-center justify-center">
                +91
              </div>
              <div className="px-2 border border-gray-400 rounded-full w-full h-[40px] flex items-center justify-center">
                <input
                  type="tel"
                  name="phoneNo"
                  id="search-dropdown"
                  className="p-2.5 w-full text-base focus:outline-none rounded focus:bg-transparent autofill:bg-transparent"
                  placeholder="Enter Phone No"
                  onChange={handleInpChange}
                  onKeyDown={handleKeyDown}
                  maxLength={10}
                  autoFocus
                />
              </div>
            </div>

            <CommonText size="sm" className="mt-6 text-gray-500">
              By continuing, you agree to our{" "}
              <span className="font-bold text-gray-800">Terms & Conditions</span> and{" "}
              <span className="font-bold text-gray-800">Privacy Policy</span>
            </CommonText>
          </>
        )}
        
        {/* OTP */}
        {text.checkPath === CheckPath.OtpPage && (
          <>
            <CommonText size="lg" className="font-medium">
            Verify your phone number with OTP
            </CommonText>
            <CommonText size="base" className="mt-8 tracking-wider">
          OTP sent on Whatsapp: {formData?.phoneNo}.
            </CommonText>
            <div className="mt-12">
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                renderSeparator={<div className="w-[16px]" />}
                renderInput={(
                  props: React.InputHTMLAttributes<HTMLInputElement>,
                  index: number
                ) => (
                  <input
                    {...props}
                    name="otp"
                    id={`otp-input-${index}`}
                    className={`border rounded ${
                      otpError ? "border-red-500" : "border-gray-400"
                    }`}
                    onKeyDown={handleKeyDown}
                    autoFocus={index === otp.length}
                    inputMode="numeric"
                  />
                )}
                inputStyle={{
                  width: "100%",
                  maxWidth: "60px",
                  minWidth: "30px",
                  height: "50px",
                }}
                containerStyle={{
                  justifyContent: "center",
                  gap: "4px",
                }}
              />
              {otpError && (
                <div className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                  <svg
                    className="w-5 h-5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-sm">{otpError}</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Details Page */}
        {text.checkPath === CheckPath.DetailsPage && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <CommonText size="xl" className="tracking-wide">
                Welcome to
              </CommonText>
              <Image
                src={logo}
                alt="ridengo logo"
                className="w-[120px] h-[32px] md:w-[160px] md:h-[42px]"
                width={240}
                height={64}
              />
            </div>
            <CommonText size="base" className="mt-8 tracking-wider">
              Provide some additional info, so we can serve you better
            </CommonText>
            <CommonText size="xs" className="mt-8 mb-1">
              Name
            </CommonText>
            <div className="flex items-center w-full rounded-full border border-gray-300 pl-2">
              <input
                type="text"
                id="search-dropdown"
                className="p-2.5 w-full text-md focus:outline-none rounded"
                placeholder="Enter Name"
                onChange={handleInpChange}
                name="user_name"
              />
            </div>
            <CommonText size="xs" className="mt-4 mb-1">
              Email
            </CommonText>
            <div className="flex items-center w-full rounded-full border border-gray-300 pl-2">
              <input
                type="email"
                id="search-dropdown"
                className="p-2.5 w-full text-md focus:outline-none rounded"
                placeholder="Enter Email"
                onChange={handleInpChange}
                name="email"
              />
            </div>
          </>
        )}
        
        <button
          onClick={handleLoginBtn}
          disabled={disableBtn}
          type="button"
          className={`rounded-full bg-[#F05A24] text-white h-10 w-full md:w-1/2 mt-8 ${
            disableBtn ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }`}
        >
          {text.btnText}
        </button>
      </div>
    </div>
  );
};

const LoginFlowModal = ({
  setModalOpen,
  setLoginProfileBtnText,
  setUserData,
  isBooking,
  forceDetailsPage = false,
}: {
  setModalOpen: (open: boolean) => void;
  setLoginProfileBtnText: (text: string) => void;
  setUserData: (userData: { name: string; email: string; mobile: string; _id: string }) => void;
  isBooking: boolean;
  forceDetailsPage: boolean;
}) => {
  const isMobile = useIsMobile();
  const router = useRouter(); 
  
  // Create a wrapper function to log modal state changes
  const handleModalClose = (open: boolean) => {
    setModalOpen(open);
  };

  const [text, setText] = useState({
    checkPath: CheckPath.LoginPage,
    btnText: BtnText.Login,
  });
  const [formData, setFormData] = useState({
    phoneNo: "",
    user_name: "",
    email: "",
    id: "",
  });
  const [otp, setOtp] = useState("");
  const [disableBtn, setDisableBtn] = useState(true);
  const [otpError, setOtpError] = useState("");


  // Initialize modal state based on current auth status
  useEffect(() => {
    const initializeModal = () => {
      try {
        const loginDetails = storage.getLoginDetails();
        const userData = localStorage.getItem('user_data');

        // If forceDetailsPage is true, show details page regardless of auth status
        if (forceDetailsPage && loginDetails.token && userData) {
          const parsedUserData = JSON.parse(userData);
          setText({
            btnText: BtnText.SubmitDetails,
            checkPath: CheckPath.DetailsPage,
          });
          setFormData({
            phoneNo: loginDetails.phone,
            user_name: "",
            email: parsedUserData.email || "",
            id: parsedUserData._id,
          });
          setDisableBtn(true);
          return;
        }
        
        if (loginDetails.token && userData) {
          const parsedUserData = JSON.parse(userData);
          
          // If user is logged in but has incomplete profile (missing name or email), show details page
          if (!parsedUserData.name || !parsedUserData.email) {
            setText({
              btnText: BtnText.SubmitDetails,
              checkPath: CheckPath.DetailsPage,
            });
            setFormData({
              phoneNo: loginDetails.phone,
              user_name: parsedUserData.name || "",
              email: parsedUserData.email || "",
              id: parsedUserData._id,
            });
            setDisableBtn(false); // Enable button for profile completion
          } else {
            // User is fully logged in with complete profile
            // Only close if not forceDetailsPage AND not in the middle of login process
            if (!forceDetailsPage && text.checkPath === CheckPath.LoginPage) {
              // Don't close modal if we're in the middle of OTP verification
              // Preload Razorpay script for returning users with complete profiles
              if (isBooking) {
                rzploadScript().catch((error) => {
                  console.warn("Failed to preload Razorpay script:", error);
                });
                
                // Dispatch proceedToPayment event for returning users with complete profiles
                try {
                  const event = new CustomEvent('proceedToPayment', {
                    detail: {
                      userData: {
                        name: parsedUserData.name,
                        email: parsedUserData.email,
                        mobile: parsedUserData.mobile || loginDetails.phone,
                        _id: parsedUserData._id,
                      }
                    }
                  });
                  window.dispatchEvent(event);
                } catch (e) {
                  console.error('Failed to dispatch proceedToPayment event:', e);
                }
              }
              setModalOpen(false);
            } else if (forceDetailsPage) {
              // Even with complete profile, show details page if forceDetailsPage is true
              setText({
                btnText: BtnText.SubmitDetails,
                checkPath: CheckPath.DetailsPage,
              });
              setFormData({
                phoneNo: loginDetails.phone,
                user_name: parsedUserData.name || "",
                email: parsedUserData.email || "",
                id: parsedUserData._id,
              });
              setDisableBtn(false);
            }
          }
        } else {
          // User not logged in, start with login page
          setText({
            btnText: BtnText.Login,
            checkPath: CheckPath.LoginPage,
          });
          setFormData({
            phoneNo: "",
            user_name: "",
            email: "",
            id: "",
          });
          setDisableBtn(true);
        }
      } catch (error) {
        console.error('Error initializing modal:', error);
        // Fallback to login page
        setText({
          btnText: BtnText.Login,
          checkPath: CheckPath.LoginPage,
        });
        setDisableBtn(true);
      }
    };

    // Only initialize if we're on the login page (not in the middle of OTP verification)
    if (text.checkPath === CheckPath.LoginPage) {
      initializeModal();
    }
  }, [setModalOpen, forceDetailsPage, text.checkPath, isBooking]);

  const handleLoginBtn = async () => {
    try {
      switch (text.checkPath) {
        case CheckPath.LoginPage:
          await handleSendOtp();
          break;
        case CheckPath.OtpPage:
          await handleVerifyOtp();
          break;
        case CheckPath.DetailsPage:
          await handleSubmitUserDetails();
          break;
        default:
          console.error("Invalid login step");
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  // Step 1: Send OTP
  const handleSendOtp = async () => {
    const response = await API.user.SendOtp({ phoneNo: formData.phoneNo });

    if (!API.validators.checkSuccessCode(response)) {
      console.error("Failed to send OTP. Please try again.");
      return;
    }

    setOtpError(""); // Clear any previous error when moving to OTP page
    setText({ checkPath: CheckPath.OtpPage, btnText: BtnText.OtpVerify });
    setDisableBtn(true);
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async () => {
    setOtpError(""); // Clear any previous error
    const response = await API.user.SubmitOtp({
      phoneNo: formData.phoneNo,
      otp,
    });

    if (!API.validators.checkSuccessCode(response)) {
      setOtpError("Invalid OTP. Please check and try again.");
      return;
    }

    const userDetails = response.data.data?.userDetails;
    const token = response.data.data?.token;

    // Update form data with new values
    const updatedFormData = {
      ...formData,
      id: userDetails?._id,
      user_name: userDetails?.name,
    };

    setFormData(updatedFormData);

    // Login user with proper user data
    const userDataToStore = {
      name: userDetails?.name,
      email: userDetails?.email,
      _id: userDetails?._id,
      mobile: userDetails?.mobile || formData.phoneNo,
    };

    try {
      await login({
        phone: formData.phoneNo,
        token,
        userData: userDataToStore,
        onSuccess: () => {
          
          // Verify the stored data is accessible
          const verifiedUserData = verifyStoredUserData();
          
          if (!isBooking && setLoginProfileBtnText) {
            setLoginProfileBtnText("profile");
          }

          if (userDetails?.name && verifiedUserData) {
            track('login', { method: 'OTP' });
            if (isBooking && setUserData) {
              setUserData(userDataToStore);
              // Preload Razorpay script for returning users before closing modal
              rzploadScript().catch((error) => {
                console.warn("Failed to preload Razorpay script:", error);
              });
              
              // Dispatch proceedToPayment event for users with complete profiles
              try {
                const event = new CustomEvent('proceedToPayment', {
                  detail: {
                    userData: userDataToStore
                  }
                });
                window.dispatchEvent(event);
              } catch (e) {
                console.error('Failed to dispatch proceedToPayment event:', e);
              }
            }
            
            // Close modal and redirect
            setModalOpen(false);
            
            // Redirect to profile page
            if (!isBooking) {
              setTimeout(() => {
                router.push(`/userProfile/${userDetails?._id}?source=header`);
              }, 1000); // Increased delay to ensure data is stored
            }
          } else {
            // User needs to complete profile
            setText({
              checkPath: CheckPath.DetailsPage,
              btnText: BtnText.SubmitDetails,
            });
            setDisableBtn(false); // Enable button for details submission
          }
        },
        onError: (error) => {
          console.error('Login failed:', error);
          // Don't close modal on error
        }
      });
    } catch (error) {
      console.error('Error during login process:', error);
      // Don't close modal on error
    }
  };

  // Step 3: Submit user details
  const handleSubmitUserDetails = async () => {
    const response = await API.user.UpdateUserDetails({
      id: formData.id || "",
      name: formData.user_name,
      email: formData.email,
    });

    if (!API.validators.checkSuccessCode(response)) {
      console.error("Failed to update user details. Please try again.");
      return;
    }

    // Update user data
    await login({
      phone: formData.phoneNo,
      token: storage.getLoginDetails().token,
      userData: {
        name: formData.user_name,
        email: formData.email,
        mobile: formData.phoneNo, // Include mobile number
        _id: response?.data?.data?._id,
      },
      onSuccess: () => {
        track('sign_up', { method: 'OTP' });
        if (isBooking && setUserData) {
          setUserData({
            name: formData.user_name,
            email: formData.email,
            mobile: response?.data?.data?.mobile,
            _id: response?.data?.data?._id,
          });
        }
        setModalOpen(false);
        
        // If we are in booking flow, trigger payment immediately after details submission
        if (isBooking) {
          try {
            const event = new CustomEvent('proceedToPayment', {
              detail: {
                userData: {
                  name: formData.user_name,
                  email: formData.email,
                  mobile: response?.data?.data?.mobile,
                  _id: response?.data?.data?._id,
                }
              }
            });
            window.dispatchEvent(event);
          } catch (e) {
            console.error('Failed to dispatch proceedToPayment event:', e);
          }
          return;
        }
        
        // Redirect to profile page
        if (!isBooking) {
          setTimeout(() => {
            router.push(`/userProfile/${response?.data?.data?._id}?source=header`);
          }, 500);
        }
      },
      onError: (error) => {
        console.error('Failed to update user data:', error);
      }
    });
  };

  const handleInpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "phoneNo") {
      setDisableBtn(!ValidateMobileNumber(value));
    } else if (text.checkPath === CheckPath.DetailsPage) {
      // Use the updated values for validation
      const updatedName = name === "user_name" ? value : formData.user_name;
      const updatedEmail = name === "email" ? value : formData.email;
      setDisableBtn(
        !(NameValidation(updatedName) && validateEmail(updatedEmail))
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Tab", "Enter"];
    const isNumericKey = /[0-9]/.test(e.key);
    
    if (text.checkPath === CheckPath.LoginPage || text.checkPath === CheckPath.OtpPage) {
      if (!isNumericKey && !allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    }

    if (e.key === "Enter") {
      if (text.checkPath === CheckPath.LoginPage && 
          formData.phoneNo.length === 10 && 
          ValidateMobileNumber(formData.phoneNo)) {
        handleLoginBtn();
      } else if (text.checkPath === CheckPath.OtpPage && otp.length === 6) {
        handleLoginBtn();
      }
    }
  };

  useEffect(() => {
    setDisableBtn(otp.length !== 6);
    // Clear error when OTP changes
    setOtpError("");
  }, [otp]);

  // Helper function to create heading with logo
  const createHeadingWithLogo = () => (
    <div className="flex items-center gap-2">
      <Image
        src={logo}
        alt="ridengo"
        className="w-24 h-8"
        width={120}
        height={24}
      />
      {/* <CommonText size="lg" className="text-[#f05a24] font-bold">
        {headingText}
      </CommonText> */}
    </div>
  );

  // Get heading text based on current path
  const getHeadingText = () => {
    switch (text.checkPath) {
      case CheckPath.LoginPage:
        return createHeadingWithLogo();
      case CheckPath.OtpPage:
        return createHeadingWithLogo();
      default:
        return "Enter Details";
    }
  };

  return (
    <>
      {isMobile ? (
        <ModalLayoutBottomFixed
          setCloseModal={handleModalClose}
          notShowCrossBtn={text.checkPath === CheckPath.DetailsPage}
          headingText={getHeadingText()}
        >
          <ModalContent 
            formData={formData}
            text={text}
            disableBtn={disableBtn}
            otp={otp}
            handleInpChange={handleInpChange}
            handleKeyDown={handleKeyDown}
            handleLoginBtn={handleLoginBtn}
            setOtp={setOtp}
            otpError={otpError}
          />
        </ModalLayoutBottomFixed>
      ) : (
        <ModalLayout
          setCloseModal={handleModalClose}
          notShowCrossBtn={text.checkPath === CheckPath.DetailsPage}
        >
          <ModalContent 
            formData={formData}
            text={text}
            disableBtn={disableBtn}
            otp={otp}
            handleInpChange={handleInpChange}
            handleKeyDown={handleKeyDown}
            handleLoginBtn={handleLoginBtn}
            setOtp={setOtp}
            otpError={otpError}
          />
        </ModalLayout>
      )}
    </>
  );
};

export default LoginFlowModal;
